import React, { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useAuth0 } from '@auth0/auth0-react'

import SortableTable from '../../components/CommonComponents/SortableTableComponent'
import { getPerformanceSegment } from '../../redux/actions/accountHealth'
import { calcDerivedMetrics, formatCurrency, formatValue, tableSorter } from '../../services/helper'

const columns = [
  { key: 'label', name: 'Label', className: 'col-label' },
  { key: 'count', name: 'Target Count' },
  { key: 'bid', name: 'Average Bid' },
  { key: 'minBid', name: 'Min Bid' },
  { key: 'maxBid', name: 'Max Bid' },
  { key: 'impressions', name: 'Impression' },
  { key: 'clicks', name: 'Click' },
  { key: 'costPercent', name: '% Spend' },
  { key: 'cost', name: 'Spend' },
  { key: 'orders', name: 'Order' },
  { key: 'revenuePercent', name: '% Sales' },
  { key: 'revenue', name: 'Sales' },
  { key: 'ctr', name: 'CTR' },
  { key: 'cpc', name: 'CPC' },
  { key: 'acos', name: 'ACoS' },
]

/**
 * PerformanceSegment Component
 *
 * A reusable component for displaying performance segment data with flexible filtering options.
 *
 * Props:
 * @param {boolean} fromBulkEngine - Whether component is used in bulk engine context
 * @param {string} className - Additional CSS classes
 * @param {object} customFilters - Custom filters to be sent with API request
 *   Example: { campaignIds: [1, 2, 3], campaignType: 'auto', groupBy: 'portfolio' }
 * @param {function} customApiCall - Custom API call function if different endpoint needed
 *   Signature: (accessToken, signal, filters) => Promise<data>
 * @param {object} externalData - Pre-loaded data to skip API call
 * @param {function} onDataLoad - Callback when data is loaded
 */
const PerformanceSegment = ({
  fromBulkEngine = false,
  className = '',
  customFilters = null,
  customApiCall = null,
  externalData = null,
  onDataLoad = null,
}) => {
  const dispatch = useDispatch()
  const { getAccessTokenSilently } = useAuth0()

  const currentUserId = useSelector(state => state.header.currentUserId)
  const globalStartDate = useSelector(state => state.accountHealth.startDate)
  const globalEndDate = useSelector(state => state.accountHealth.endDate)
  const currencyRate = useSelector(state => state.header.currencyRate)
  const currencySign = useSelector(state => state.header.currencySign)

  // Use dates from customFilters when in BulkEngine context, otherwise use global dates
  const startDate = fromBulkEngine && customFilters?.startDate ? customFilters.startDate : globalStartDate
  const endDate = fromBulkEngine && customFilters?.endDate ? customFilters.endDate : globalEndDate

  const [isLoading, setIsLoading] = useState(false)
  const [records, setRecords] = useState([])
  const [summary, setSummary] = useState({})

  // Add refs for request management
  const requestIdRef = useRef(0)
  const abortControllerRef = useRef(null)
  const lastRequestParamsRef = useRef(null)

  useEffect(() => {
    // If external data is provided, use it directly
    if (externalData) {
      parseData(externalData)
      return
    }

    if (!currentUserId || isLoading) return

    // Create request parameters for comparison
    const currentParams = JSON.stringify({
      currentUserId,
      startDate,
      endDate,
      customFilters
    })

    // Skip if parameters haven't actually changed
    if (lastRequestParamsRef.current === currentParams) {
      return
    }

    // Debounce rapid changes (like date picker dragging)
    const timeoutId = setTimeout(async () => {
      // Double-check parameters haven't changed during timeout
      const latestParams = JSON.stringify({
        currentUserId,
        startDate,
        endDate,
        customFilters
      })
      
      if (latestParams !== currentParams) {
        return // Skip if params changed during timeout
      }

      // Increment request ID for tracking
      const requestId = ++requestIdRef.current
      lastRequestParamsRef.current = currentParams

      // Abort previous request only now
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      // Create new abort controller
      abortControllerRef.current = new AbortController()
      
      try {
        const accessToken = await getAccessTokenSilently()
        setIsLoading(true)

        let data
        // Remove refreshCounter from filters before sending to API (it's only for frontend refresh logic)
        const apiFilters = customFilters ? { ...customFilters } : null
        if (apiFilters && 'refreshCounter' in apiFilters) {
          delete apiFilters.refreshCounter
        }
        
        if (customApiCall) {
          data = await customApiCall(accessToken, abortControllerRef.current.signal, apiFilters)
        } else {
          data = await dispatch(getPerformanceSegment(
            accessToken,
            abortControllerRef.current.signal,
            apiFilters
          ))
        }

        // Only update if this is still the latest request
        if (requestId === requestIdRef.current && !abortControllerRef.current.signal.aborted) {
          parseData(data)
          if (onDataLoad) {
            onDataLoad(data)
          }
        }
      } catch (error) {
        // Only show error if request wasn't aborted
        if (!abortControllerRef.current.signal.aborted) {
          console.error('Performance segment request failed:', error)
        }
      } finally {
        // Only update loading state if this is still the latest request
        if (requestId === requestIdRef.current) {
          setIsLoading(false)
        }
      }
    }, 300) // 300ms debounce

    return () => {
      clearTimeout(timeoutId)
    }
  }, [currentUserId, startDate, endDate, customFilters, externalData, isLoading, dispatch, getAccessTokenSilently, customApiCall, onDataLoad])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  const parseData = (data) => {
    if (!data) {
      setRecords([])
      return
    }

    const buckets = [
      { key: 'acos10', label: 'ACoS 0-10%' },
      { key: 'acos20', label: 'ACoS 10-20%' },
      { key: 'acos30', label: 'ACoS 20-30%' },
      { key: 'acos40', label: 'ACoS 30-40%' },
      { key: 'acos50', label: 'ACoS 40-50%' },
      { key: 'acos60', label: 'ACoS 50-60%' },
      { key: 'acos70', label: 'ACoS 60-70%' },
      { key: 'acos80', label: 'ACoS 70-80%' },
      { key: 'acos90', label: 'ACoS 80-90%' },
      { key: 'acos100', label: 'ACoS 90-100%' },
      { key: 'acos120', label: 'ACoS 100-120%' },
      { key: 'acos150', label: 'ACoS 120-150%' },
      { key: 'acos200', label: 'ACoS 150-200%' },
      { key: 'acos200+', label: '> 200% ACoS' },
      { key: 'clicks5', label: '< 5 Clicks, No Orders' },
      { key: 'clicks10', label: '5-9 Clicks, No Orders' },
      { key: 'clicks15', label: '10-14 Clicks, No Orders' },
      { key: 'clicks15+', label: '> 15 Clicks, No Orders' },
      { key: 'impressions50', label: '< 50 Impressions' },
      { key: 'no_spend', label: 'No Spends' },
    ]

    let totalCount = 0
    let totalRevenue = 0
    let totalCost = 0
    setRecords(buckets.map((bucket, index) => {
      const record = calcDerivedMetrics(data.buckets[bucket.key])

      totalCount += record.count
      totalRevenue += record.revenue
      totalCost += record.cost

      return {
        ...record,
        sort: index,
        key: bucket.key,
        label: bucket.label,
        revenuePercent: data.totalRevenue
          ? record.revenue / data.totalRevenue * 100
          : 0,
        costPercent: data.totalCost
          ? record.cost / data.totalCost * 100
          : 0,
      }
    }))

    setSummary({
      count: totalCount,
      revenuePercent: data.totalRevenue
        ? totalRevenue / data.totalRevenue * 100
        : 0,
      costPercent: data.totalCost
        ? totalCost / data.totalCost * 100
        : 0,
    })
  }

  const renderRecord = record => (
    <>
      <div className="table-col col-label">{ record.label }</div>
      <div className="table-col">
        { formatValue(record.count, 'removeZeroDecimal') }
      </div>
      <div className="table-col">
        { formatCurrency(record.bid, currencySign, currencyRate) }
      </div>
      <div className="table-col">
        { formatCurrency(record.minBid, currencySign, currencyRate) }
      </div>
      <div className="table-col">
        { formatCurrency(record.maxBid, currencySign, currencyRate) }
      </div>
      <div className="table-col">
        { formatValue(record.impressions, 'removeZeroDecimal') }
      </div>
      <div className="table-col">
        { formatValue(record.clicks, 'removeZeroDecimal') }
      </div>
      <div className="table-col">
        { formatValue(record.costPercent, 'percent') }
      </div>
      <div className="table-col">
        { formatCurrency(record.cost, currencySign, currencyRate, 2, true) }
      </div>
      <div className="table-col">
        { formatValue(record.orders, 'removeZeroDecimal') }
      </div>
      <div className="table-col">
        { formatValue(record.revenuePercent, 'percent') }
      </div>
      <div className="table-col">
        { formatCurrency(record.revenue, currencySign, currencyRate, 2, true) }
      </div>
      <div className="table-col">
        { formatValue(record.ctr, 'percent') }
      </div>
      <div className="table-col">
        { formatCurrency(record.cpc, currencySign, currencyRate) }
      </div>
      <div className="table-col">
        { formatValue(record.acos, 'percent') }
      </div>
    </>
  )

  const renderTotal = record => (
    <>
      <div className="table-col col-label">Totals:</div>
      <div className="table-col">
        { formatValue(summary.count || 0, 'removeZeroDecimal') }
      </div>
      <div className="table-col">
      </div>
      <div className="table-col">
      </div>
      <div className="table-col">
      </div>
      <div className="table-col">
        { formatValue(record.impressions, 'removeZeroDecimal') }
      </div>
      <div className="table-col">
        { formatValue(record.clicks, 'removeZeroDecimal') }
      </div>
      <div className="table-col">
        { formatValue(summary.costPercent || 0, 'percent') }
      </div>
      <div className="table-col">
        { formatCurrency(record.cost, currencySign, currencyRate, 2, true) }
      </div>
      <div className="table-col">
        { formatValue(record.orders, 'removeZeroDecimal') }
      </div>
      <div className="table-col">
        { formatValue(summary.revenuePercent || 0, 'percent') }
      </div>
      <div className="table-col">
        { formatCurrency(record.revenue, currencySign, currencyRate, 2, true) }
      </div>
      <div className="table-col">
        { formatValue(record.ctr, 'percent') }
      </div>
      <div className="table-col">
        { formatCurrency(record.cpc, currencySign, currencyRate) }
      </div>
      <div className="table-col">
        { formatValue(record.acos, 'percent') }
      </div>
    </>
  )

  return (
    <div className={`report-widget ${className}`}>
      <div className="report-widget-header">
        <div className="widget-title">
          <span>
            Performance Segments
          </span>
        </div>
      </div>
      <div className="report-widget-content">
        <SortableTable
          isLoading={isLoading}
          columns={columns}
          defaultSort={['sort', 'asc']}
          sorter={tableSorter(['label'])}
          className="table-performance-segment"
          records={records}
          idField="key"
          noSearch
          noCheckBox
          noPagination
          renderRecord={renderRecord}
          renderTotal={renderTotal}
        />
      </div>
    </div>
  )
}

/**
 * Helper function to create custom filters for common use cases
 */
export const createPerformanceSegmentFilters = ({
  campaignIds = null,
  campaignType = null, // 'auto', 'manual'
  portfolioIds = null,
  adGroupIds = null,
  startDate = null,
  endDate = null,
  refreshCounter = null, // Force refresh even with same parameters
}) => {
  const filters = {}

  if (campaignIds && campaignIds.length > 0) {
    filters.campaignIds = campaignIds
  }

  if (campaignType) {
    filters.campaignType = campaignType
  }

  if (portfolioIds && portfolioIds.length > 0) {
    filters.portfolioIds = portfolioIds
  }

  if (adGroupIds && adGroupIds.length > 0) {
    filters.adGroupIds = adGroupIds
  }

  if (startDate) {
    filters.startDate = startDate
  }

  if (endDate) {
    filters.endDate = endDate
  }

  // Include refresh counter to force new requests even with identical parameters
  if (refreshCounter !== null) {
    filters.refreshCounter = refreshCounter
  }

  return Object.keys(filters).length > 0 ? filters : null
}

export default PerformanceSegment
